import { useEffect, useState } from 'react';

/**
 * Tracks conversations that received a background PAI response while the user
 * was NOT viewing them — the "fire-and-forget" green dot (Multiple messages
 * multiple conversations spec).
 *
 * Persisted in localStorage (keyed per conversation) so the indicator survives
 * reloads and is shared across components via a window event, mirroring the
 * orange-draft-dot pattern in useConversationDraft. The dot is solid green and
 * non-blinking; it's set when a response is committed to a non-active
 * conversation and cleared the instant the user opens that conversation.
 */
const RESPONDED_KEY = 'omnis:responded-conversations';
const RESPONDED_EVENT = 'omnis:responded-changed';

const readSet = () => {
    try {
        const raw = localStorage.getItem(RESPONDED_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr : []);
    } catch (_) {
        return new Set();
    }
};

const writeSet = (set) => {
    try {
        localStorage.setItem(RESPONDED_KEY, JSON.stringify(Array.from(set)));
    } catch (_) { /* ignore */ }
    try {
        window.dispatchEvent(new CustomEvent(RESPONDED_EVENT));
    } catch (_) { /* ignore */ }
};

/** Mark a conversation as having an unread background response (green dot). */
export function markConversationResponded(conversationId) {
    if (!conversationId) return;
    const set = readSet();
    if (set.has(conversationId)) return;
    set.add(conversationId);
    writeSet(set);
}

/** Clear the green dot — call when the user opens the conversation. */
export function clearConversationResponded(conversationId) {
    if (!conversationId) return;
    const set = readSet();
    if (!set.has(conversationId)) return;
    set.delete(conversationId);
    writeSet(set);
}

/** Reactive Set of conversation ids with an unread background response. */
export function useRespondedConversationIds() {
    const [ids, setIds] = useState(() => readSet());
    useEffect(() => {
        const recompute = () => setIds(readSet());
        window.addEventListener(RESPONDED_EVENT, recompute);
        window.addEventListener('storage', recompute);
        return () => {
            window.removeEventListener(RESPONDED_EVENT, recompute);
            window.removeEventListener('storage', recompute);
        };
    }, []);
    return ids;
}
